/**
 * useReports.ts — State and submit logic for the Report Generator module.
 */
import { useCallback, useState } from 'react'
import { reports } from '@/api/client'
import type { ReportOptions } from '@/types'
import { DEFAULT_REPORT_OPTIONS } from '@/types'
import { useJobPoller } from './useJobPoller'

export function useReports() {
  const [brief,   setBrief]   = useState('')
  const [options, setOptions] = useState<ReportOptions>(DEFAULT_REPORT_OPTIONS)

  const { jobView, beginJob, cancel, reset, setError, openJob } = useJobPoller({
    getJob:    reports.getJob,
    cancelJob: reports.cancelJob,
  })

  const submit = useCallback(async () => {
    if (!brief.trim()) return
    try {
      const isTenant = options.mode === 'tenant'
      const job = await reports.createJob({
        text:          brief.trim(),
        mode:          options.mode,
        tenant:        isTenant ? (options.tenant || 'default') : undefined,
        data_source:   isTenant ? undefined : options.data_source,
        date_from:     isTenant ? undefined : (options.date_from || undefined),
        date_to:       isTenant ? undefined : (options.date_to   || undefined),
        pages:         options.pages,
        tone:          options.tone,
        audience:      options.audience || undefined,
        gl:            options.gl,
        skip_research: isTenant ? false : options.skip_research,
        plausible:     options.plausible,
        evidence:      options.evidence,
      })
      await beginJob(job.job_id)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start generation.')
    }
  }, [brief, options, beginJob, setError])

  return { brief, setBrief, options, setOptions, jobView, submit, cancel, reset, openJob }
}
